import React, { useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PendingApproval from './pages/PendingApproval';
import Dashboard from './pages/Dashboard';
import SupervisorDashboard from './pages/SupervisorDashboard';
import { initDevToolsProtection, destroyDevToolsProtection } from './utils/devToolsProtection';
import { ROLES } from './services/authService';

function App() {
  const [appPhase, setAppPhase] = useState('login'); // 'login' | 'app'
  const [user, setUser] = useState(null);
  const [loginVisible, setLoginVisible] = useState(true);
  const [showSupervisorDash, setShowSupervisorDash] = useState(false);

  // ✅ Request-access flow: which pre-login screen to show, and whether a
  // real (email) account is stuck waiting for admin approval.
  const [authScreen, setAuthScreen] = useState('login'); // 'login' | 'register'
  const [accessStatus, setAccessStatus] = useState(null); // null | 'pending' | 'denied'

  // ✅ REMOVED: anonymous sign-in. It was only ever a bridge to satisfy
  // security rules for PIN-based login. Now that PIN is fully retired and
  // every real user goes through Firebase Auth, this is no longer needed —
  // and removing it means Firestore rules can require a REAL, identifiable
  // login for everything, with no anonymous loophole left at all.

  // ✅ Request-access flow: watches for a Firebase login and checks
  // whether an admin has approved that account yet.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        return;
      }
      try {
        const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (!snap.exists()) {
          setAccessStatus('pending');
          return;
        }
        const data = snap.data();
        if (data.approvalStatus === 'approved' && data.active !== false) {
          const userData = { id: firebaseUser.uid, ...data };
          localStorage.setItem('currentUser', JSON.stringify(userData));
          setUser(userData);
          setAccessStatus(null);
          setAppPhase('app');
          initDevToolsProtection(userData);
        } else if (data.approvalStatus === 'denied') {
          setAccessStatus('denied');
        } else {
          setAccessStatus('pending');
        }
      } catch (err) {
        console.error('Error checking account approval status:', err);
      }
    });
    return unsubscribe;
  }, []);

  // Restore session on page refresh
  useEffect(() => {
    const saved = localStorage.getItem('currentUser');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setUser(parsed);
        setAppPhase('app');
        initDevToolsProtection(parsed);
      } catch {
        localStorage.removeItem('currentUser');
      }
    }
  }, []);

  // ✅ REMOVED: handleLogin (was for PIN login). Email sign-in is handled
  // entirely by the onAuthStateChanged listener above — LoginPage no
  // longer needs to hand a user object back up to App.js directly.

  const handleLogout = () => {
    destroyDevToolsProtection();
    localStorage.removeItem('currentUser');
    setUser(null);
    setAppPhase('login');
    setLoginVisible(true);
    setShowSupervisorDash(false);
    setAccessStatus(null);
    setAuthScreen('login');

    signOut(auth).catch(() => {});
  };

  // ── Render ────────────────────────────────────────────────────────────────
  // A real (email) account that isn't approved yet takes priority over
  // everything else — they should see this no matter what phase the rest
  // of the app thinks it's in.
  if (accessStatus === 'pending' || accessStatus === 'denied') {
    return <PendingApproval status={accessStatus} onLogout={handleLogout} />;
  }

  if (appPhase === 'app' && user) {
    const role = user.role?.toLowerCase();
    
    // Check if user has supervisor/admin privileges
    const isSupervisorOrAdmin = 
      role === ROLES.SUPERVISOR || 
      role === ROLES.ADMIN || 
      role === ROLES.SUPER_ADMIN;

    // Supervisor / Admin / Super Admin viewing their dashboard
    if (isSupervisorOrAdmin && showSupervisorDash) {
      return (
        <SupervisorDashboard
          user={user}
          onBack={() => setShowSupervisorDash(false)}
          onLogout={handleLogout}
          onUserUpdate={setUser}
        />
      );
    }

    // All roles land on main Dashboard first
    return (
      <Dashboard
        user={user}
        onLogout={handleLogout}
        onOpenSupervisorDash={isSupervisorOrAdmin ? () => setShowSupervisorDash(true) : undefined}
        onUserUpdate={setUser}
      />
    );
  }

  return (
    <div className={loginVisible ? 'login-slide-up' : ''} style={{ opacity: loginVisible ? 1 : 0 }}>
      {authScreen === 'register' ? (
        <RegisterPage onBackToLogin={() => setAuthScreen('login')} />
      ) : (
        <LoginPage onRequestAccess={() => setAuthScreen('register')} />
      )}
    </div>
  );
}

export default App;
